/**
 * Mad Embed – GDPR/DSGVO compliant third party embeds.
 *
 * The iframe ships inside an inert <template>, so nothing is requested from the
 * provider before consent. Consent can come from the visitor (button click) or
 * from a cookie banner through the global `window.madEmbedConsent` API.
 *
 * Public API
 * ──────────────────────────────────────────────────────────────────────────
 *   window.madEmbedConsent.grant(service)
 *   window.madEmbedConsent.revoke(service)
 *   window.madEmbedConsent.isGranted(service)
 *   window.madEmbedConsent.grantAll()
 *   window.madEmbedConsent.revokeAll()
 *   window.madEmbedConsent.services()   // services present on the page
 *   window.madEmbedConsent.granted()    // currently granted services
 *   window.madEmbedConsent.refresh(root)// pick up dynamically added embeds
 *   window.madEmbedConsent.on(event, callback) / .off(event, callback)
 *
 * Events (document, CustomEvent with `detail`)
 *   mad-embed:request  – visitor asked to load an embed  { service, element }
 *   mad-embed:granted  – consent granted                 { service }
 *   mad-embed:revoked  – consent withdrawn               { service }
 *
 * jQuery bridge (kept in sync with the video component convention)
 *   trigger  madRequestEmbedConsent          { service }
 *   listens  madEmbedConsentGiven:<service>
 *   listens  madVideoConsentGiven:<service>  (shared providers, e.g. youtube)
 */

var STORAGE_KEY = 'mad_embed_consent';
var SELECTOR = '[data-mad-embed]';

var instances = [];
var granted = Object.create(null);
var listeners = Object.create(null);

/* ------------------------------------------------------------------ *
 *  Storage – best effort, never throws (private mode, disabled storage)
 * ------------------------------------------------------------------ */

function readStorage() {
  try {
    var raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    var parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(function (item) {
      return typeof item === 'string';
    }) : [];
  } catch (e) {
    return [];
  }
}

function writeStorage(services) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(services));
  } catch (e) {
    /* storage unavailable – consent stays valid for this page view only */
  }
}

function persist(service) {
  var stored = readStorage();
  if (stored.indexOf(service) === -1) {
    stored.push(service);
    writeStorage(stored);
  }
}

function unpersist(service) {
  var stored = readStorage().filter(function (item) {
    return item !== service;
  });
  writeStorage(stored);
}

/* ------------------------------------------------------------------ *
 *  Event helpers
 * ------------------------------------------------------------------ */

function emit(name, detail) {
  var callbacks = listeners[name];
  if (callbacks) {
    callbacks.slice().forEach(function (callback) {
      try {
        callback(detail);
      } catch (e) {
        console.error('madEmbedConsent listener failed:', e);
      }
    });
  }

  try {
    document.dispatchEvent(new CustomEvent('mad-embed:' + name, {
      detail: detail,
      bubbles: true
    }));
  } catch (e) {
    /* CustomEvent unsupported – the callback API above still works */
  }
}

function jq() {
  return window.jQuery || null;
}

/* ------------------------------------------------------------------ *
 *  MadEmbed – one instance per [data-mad-embed] element
 * ------------------------------------------------------------------ */

function MadEmbed(element) {
  this.element = element;
  this.service = element.getAttribute('data-embed-service') || 'custom';
  this.serviceLabel = element.getAttribute('data-embed-service-label') || this.service;

  this.template = element.querySelector('.mad-embed__source');
  this.frame = element.querySelector('.mad-embed__frame');
  this.consent = element.querySelector('.mad-embed__consent');
  this.button = element.querySelector('[data-embed-load]');

  this.loaded = false;
  this.iframe = null;

  this.init();
}

MadEmbed.prototype.init = function () {
  if (!this.template || !this.frame) {
    return;
  }

  var self = this;

  if (this.button) {
    this.button.addEventListener('click', function (event) {
      event.preventDefault();
      self.requestConsent();
    });
  }

  this.element.setAttribute('data-mad-embed-ready', 'true');
};

/**
 * Visitor clicked the load button: announce the request so a cookie banner can
 * record it, then load. The embed loads regardless of whether a banner answers.
 */
MadEmbed.prototype.requestConsent = function () {
  emit('request', {
    service: this.service,
    element: this.element
  });

  var $ = jq();
  if ($) {
    $(document).trigger('madRequestEmbedConsent', { service: this.service });
  }

  grant(this.service);
};

/**
 * Clone the inert template into the live DOM. The markup was built and escaped
 * in PHP, so nothing here interpolates untrusted strings.
 */
MadEmbed.prototype.load = function (options) {
  if (this.loaded || !this.template || !this.frame) {
    return;
  }

  this.loaded = true;

  var fragment = this.template.content
    ? this.template.content.cloneNode(true)
    : null;

  if (!fragment) {
    this.loaded = false;
    return;
  }

  this.frame.appendChild(fragment);
  this.iframe = this.frame.querySelector('.mad-embed__iframe');
  this.element.classList.add('mad-embed--loaded');

  if (this.consent) {
    this.consent.setAttribute('hidden', 'hidden');
  }

  // Only move focus when the visitor triggered the load themselves.
  if (options && options.focus && this.iframe) {
    this.iframe.setAttribute('tabindex', '-1');
    this.iframe.focus({ preventScroll: true });
  }
};

/**
 * Remove the iframe again and show the consent overlay. Used when consent is
 * withdrawn in the cookie banner.
 */
MadEmbed.prototype.unload = function () {
  if (!this.loaded) {
    return;
  }

  if (this.iframe && this.iframe.parentNode) {
    // Blank the source first so the frame stops loading immediately.
    this.iframe.setAttribute('src', 'about:blank');
    this.iframe.parentNode.removeChild(this.iframe);
  }

  this.iframe = null;
  this.loaded = false;
  this.element.classList.remove('mad-embed--loaded');

  if (this.consent) {
    this.consent.removeAttribute('hidden');
  }
};

/* ------------------------------------------------------------------ *
 *  Consent controller
 * ------------------------------------------------------------------ */

function forEachInstance(service, callback) {
  instances.forEach(function (instance) {
    if (!service || instance.service === service) {
      callback(instance);
    }
  });
}

function grant(service, options) {
  if (!service) return;

  var settings = options || {};
  var alreadyGranted = granted[service] === true;

  granted[service] = true;

  persist(service);

  forEachInstance(service, function (instance) {
    instance.load({ focus: settings.focus !== false && !settings.silent });
  });

  if (!alreadyGranted) {
    emit('granted', { service: service });

    var $ = jq();
    if ($) {
      $(document).trigger('madEmbedConsentGiven:' + service, [{ service: service }]);
    }
  }
}

function revoke(service) {
  if (!service || granted[service] !== true) {
    // Still drop any persisted entry so a stale grant cannot resurface.
    unpersist(service);
    return;
  }

  delete granted[service];
  unpersist(service);

  forEachInstance(service, function (instance) {
    instance.unload();
  });

  emit('revoked', { service: service });
}

function pageServices() {
  var seen = [];
  instances.forEach(function (instance) {
    if (seen.indexOf(instance.service) === -1) {
      seen.push(instance.service);
    }
  });
  return seen;
}

/**
 * Adopt consent that was granted before this script ran, either persisted in
 * the local storage or published by a cookie banner on the window object.
 */
function adoptExistingConsent(service) {
  if (readStorage().indexOf(service) !== -1) {
    return true;
  }

  var fromEmbeds = window.madEmbedConsentGiven;
  if (fromEmbeds && fromEmbeds[service] === true) {
    return true;
  }

  // The video component uses the same provider keys – honour its consent too.
  var fromVideo = window.madVideoConsentGiven;
  if (fromVideo && fromVideo[service] === true) {
    return true;
  }

  return false;
}

function scan(root) {
  var scope = root && root.querySelectorAll ? root : document;
  var elements = scope.querySelectorAll(SELECTOR);
  var added = [];

  Array.prototype.forEach.call(elements, function (element) {
    if (element.getAttribute('data-mad-embed-ready') === 'true') {
      return;
    }

    var instance = new MadEmbed(element);
    instances.push(instance);
    added.push(instance);
  });

  added.forEach(function (instance) {
    if (granted[instance.service] === true || adoptExistingConsent(instance.service)) {
      granted[instance.service] = true;
      instance.load({ focus: false });
    }
  });

  return added.length;
}

/* ------------------------------------------------------------------ *
 *  Bridge: cookie banners that use the existing jQuery convention
 * ------------------------------------------------------------------ */

function bindConsentBridge() {
  var $ = jq();
  if (!$) return;

  // `madEmbedConsentGiven:<service>` and `madVideoConsentGiven:<service>` are
  // namespaced triggers, so resolve the service from the event namespace.
  $(document).on('madEmbedConsentGiven madVideoConsentGiven', function (event, data) {
    var service = (data && (data.service || data.type)) || event.namespace;
    if (service) {
      grant(service, { silent: true, focus: false });
    }
  });
}

/* ------------------------------------------------------------------ *
 *  Public API
 * ------------------------------------------------------------------ */

window.madEmbedConsent = {
  version: '1.0.0',

  grant: function (service, options) {
    grant(service, options || {});
  },

  revoke: function (service) {
    revoke(service);
  },

  isGranted: function (service) {
    return granted[service] === true;
  },

  grantAll: function (options) {
    pageServices().forEach(function (service) {
      grant(service, options || {});
    });
  },

  revokeAll: function () {
    Object.keys(granted).forEach(revoke);
  },

  services: pageServices,

  granted: function () {
    return Object.keys(granted);
  },

  refresh: function (root) {
    return scan(root);
  },

  on: function (event, callback) {
    if (typeof callback !== 'function') return;
    if (!listeners[event]) {
      listeners[event] = [];
    }
    listeners[event].push(callback);
  },

  off: function (event, callback) {
    if (!listeners[event]) return;
    listeners[event] = listeners[event].filter(function (registered) {
      return registered !== callback;
    });
  }
};

/* ------------------------------------------------------------------ *
 *  Boot
 * ------------------------------------------------------------------ */

function boot() {
  bindConsentBridge();
  scan(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
